/**
 * EVERYTHING, ON ONE SCREEN.
 *
 *   "with Master Login being able to access ALL cards and also see EVERYTHING
 *    from one screen"
 *
 * Reached by holding 'all', or by holding at least one permission in every
 * other card. Not by a second login (canon 1.1) — you arrive here from the same
 * portal, in the same session, under the same audit identity.
 *
 * TWO THINGS THIS SCREEN MUST NOT BECOME
 *
 * 1. A BACKDOOR AROUND THE INSIGHTS BOUNDARY. Canon 4.4: the cross-profile
 *    panels "are aggregate-only BY CONSTRUCTION — never permitted to be a click
 *    away from the identifiable record underneath without a separate,
 *    specifically-scoped permission and a fresh reason capture". Internal staff
 *    do not get a shortcut around the boundary the platform sells externally as
 *    a trust guarantee. So no figure here drills through.
 *
 * 2. A PLACE WHERE NUMBERS ARE INVENTED. The panels canon names read from
 *    tables that do not exist yet. They are named and left empty, which is what
 *    the founder asked for on the consumer side: "the 8 different sections,
 *    names as they should be, even if they're empty." A demo number on an
 *    internal dashboard is one somebody eventually repeats in a meeting.
 */

import { Link } from 'react-router-dom';
import { O, Card, StatusPill, TrustFooter } from './ui';
import { moduleCardsOf } from './lib/portal';
import { Page, PortalHeader } from './Portal';

const AGGREGATE_PANELS = [
  { title: 'Tenant PRR Distribution', note: 'Distribution across bands. Never an individual score.' },
  { title: 'Landlord Confirmation Rate', note: 'Share of confirmations made on time, platform-wide.' },
  { title: 'Provider Evidence Completion', note: 'Share of jobs closed with complete evidence.' },
];

export default function MasterDashboard({ cards, staff }) {
  const modules = moduleCardsOf(cards);

  return (
    <Page wide>
      <Link to="/" style={{ fontSize: 12.5, color: O.green, textDecoration: 'none' }}>← Portal</Link>
      <div style={{ height: 18 }} />
      {/* No strapline under the heading -- "Remove the sentence 'Everything,
          on one screen...'". The screen demonstrates it. */}
      <PortalHeader staff={staff}>
        <h1 style={{ marginTop: 10, fontSize: 20, fontWeight: 700, color: O.ink }}>
          Master Dashboard
        </h1>
      </PortalHeader>

      <section style={{ marginBottom: 30 }}>
        <h2 style={{
          fontSize: 10.5, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
          color: O.inkFaint, marginBottom: 12,
        }}>Modules · {modules.length}</h2>
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
          {modules.map((c) => (
            <Link key={c.slug} to={`/card/${c.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
              <Card style={{ padding: 15 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: O.ink }}>{c.title}</span>
                  <StatusPill tone="quiet">{c.permissions_held}</StatusPill>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </section>

      <section>
        <h2 style={{
          fontSize: 10.5, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
          color: O.inkFaint, marginBottom: 6,
        }}>Cross-profile intelligence</h2>
        <p style={{ fontSize: 11.5, lineHeight: 1.65, color: O.inkFaint, marginBottom: 14 }}>
          Aggregate only, by construction. No figure on this screen opens the record
          underneath it — that needs a separately scoped permission and a fresh
          captured reason, for staff exactly as for external Insights customers.
        </p>
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
          {AGGREGATE_PANELS.map((p) => (
            <Card key={p.title}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: O.ink }}>{p.title}</div>
              <div style={{ marginTop: 10, fontSize: 26, fontWeight: 700, color: O.inkFaint }}>—</div>
              <p style={{ marginTop: 8, fontSize: 11.5, lineHeight: 1.6, color: O.inkMuted }}>{p.note}</p>
            </Card>
          ))}
        </div>
        <p style={{ marginTop: 14, fontSize: 11.5, lineHeight: 1.65, color: O.inkFaint }}>
          These read from the jobs and evidence tables, which are not built yet. They are
          named and empty rather than filled with a plausible number: an invented figure
          on an internal dashboard is one somebody eventually repeats in a meeting.
        </p>
      </section>

      <TrustFooter />
    </Page>
  );
}
